import { Injectable, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ModalService } from '@core/modal/modal.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  ActionButtonStyles,
  DialogComponent,
  DialogService,
  IDialogConfig,
  NotificationType,
} from '@ra-web-tech-ui-toolkit/components';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { emptyAction } from '../user-data';
import { closeAllOpenDialogs, openCatalogDialog, openWebTechDialog } from './modal.action';

@Injectable({
  providedIn: 'root',
})
export class ModalEffects {
  private readonly actions$ = inject(Actions);
  private readonly modalService = inject(ModalService);
  private readonly dialogService = inject(DialogService);

  private openDialog(config: IDialogConfig, type?: NotificationType): MatDialogRef<DialogComponent> {
    switch (type) {
      case NotificationType.Success:
        return this.dialogService.openSuccessDialog(config);
      case NotificationType.Info:
        return this.dialogService.openInfoDialog(config);
      case NotificationType.Warning:
        return this.dialogService.openWarningDialog(config);
      case NotificationType.Error:
        return this.dialogService.openErrorDialog(config);
      default:
        return this.dialogService.openDialog(config);
    }
  }

  openCatalogDialog$ = createEffect(
    (): Observable<any> => {
      return this.actions$.pipe(
        ofType(openCatalogDialog),
        switchMap((payload) => {
          const modalContent = payload.payload;
          const modalResult$ = this.modalService
            .openCatalogDialog({
              OKButtonText: modalContent.OKButtonText || 'OK',
              body: modalContent.body,
              header: modalContent.header,
            })
            .confirmed$();
          return combineLatest([modalResult$]);
        }),
      );
    },
    { dispatch: false },
  );

  openWebTechDialog$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(openWebTechDialog),
      switchMap((payload) => {
        const dialogRef = this.openDialog(payload.config, payload.notificationType);
        return combineLatest([dialogRef.componentInstance.onClick, of(dialogRef), of(payload)]);
      }),
      switchMap(([dialogButton, dialogRef, payload]) => {
        if (dialogButton.buttonStyle === ActionButtonStyles.Main && payload.mainBtnHandler) {
          payload.mainBtnHandler();
        }
        return combineLatest([dialogRef.afterClosed(), of(payload)]);
      }),
      map(([_closed, payload]) => {
        if (payload.closeHandler) {
          return payload.closeHandler;
        }
        return emptyAction();
      }),
    );
  });

  closeAllOpenDialogs$ = createEffect(
    (): Observable<any> => {
      return this.actions$.pipe(
        ofType(closeAllOpenDialogs),
        map(() => {
          this.modalService.close();
        }),
      );
    },
    { dispatch: false },
  );
}
